import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  CircularProgress,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Edit, Delete, CloudSync } from '@mui/icons-material';
import { platformAccountsAPI, clientsAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { SHOW_INSTAGRAM_UI } from '../config/featureFlags';
import ConfirmDialog from './ConfirmDialog';

const platforms = (
  SHOW_INSTAGRAM_UI
    ? ['Facebook', 'Instagram', 'LinkedIn', 'X', 'YouTube', 'Tumblr']
    : ['Facebook', 'LinkedIn', 'X', 'YouTube', 'Tumblr']
);

const initialForm = {
  platform: '',
  account_name: '',
  account_id: '',
  access_token: '',
  profile_url: '',
  is_active: true,
};

function formatApiError(err) {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'string' ? x : x.msg || JSON.stringify(x))).join('; ');
  }
  return err.message || 'Request failed';
}

export default function PlatformAccountsSection({ clientId, onAccountsChanged }) {
  const { showError, showSuccess } = useToast();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [scrapingAccountId, setScrapingAccountId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    accountId: null,
  });

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const data = await platformAccountsAPI.getPlatformAccounts(clientId);
      setAccounts(data);
    } catch (err) {
      setError('Failed to load platform accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) fetchAccounts();
  }, [clientId]);

  const handleOpenForm = (account = null) => {
    if (account) {
      setForm({ ...account });
      setEditingId(account.id);
    } else {
      setForm(initialForm);
      setEditingId(null);
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setForm(initialForm);
    setEditingId(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await platformAccountsAPI.updatePlatformAccount(editingId, form);
      } else {
        await platformAccountsAPI.createPlatformAccount({
          ...form,
          client_id: clientId,
        });
      }
      await fetchAccounts();
      if (onAccountsChanged) onAccountsChanged();
      handleCloseForm();
    } catch (err) {
      setError('Failed to save platform account');
    }
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ open: true, accountId: id });
  };

  const handleConfirmDelete = async () => {
    const id = deleteConfirm.accountId;
    if (!id) return;
    setDeletingId(id);
    try {
      await platformAccountsAPI.deletePlatformAccount(id);
      await fetchAccounts();
      if (onAccountsChanged) onAccountsChanged();
      setDeleteConfirm({ open: false, accountId: null });
    } catch (err) {
      setError('Failed to delete platform account');
    } finally {
      setDeletingId(null);
    }
  };

  const handleScrapePublicProfile = async (account) => {
    if (!clientId) return;
    setScrapingAccountId(account.id);
    try {
      const data = await clientsAPI.queuePublicProfileScrape(
        clientId,
        account.id
      );
      showSuccess(
        data.message ||
          'Public Instagram scrape queued. Posts appear under client_posts (public_scrape) when done.'
      );
    } catch (err) {
      showError(formatApiError(err));
    } finally {
      setScrapingAccountId(null);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Platform Accounts
      </Typography>
      {SHOW_INSTAGRAM_UI && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          For Instagram, set profile URL or account name, then use the cloud icon to queue a
          public profile scrape (background job).
        </Typography>
      )}
      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={() => handleOpenForm()}
        sx={{ mb: 2 }}
      >
        Add Platform Account
      </Button>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>Platform</TableCell>
                <TableCell>Account Name</TableCell>
                <TableCell>Account ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Token</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>{account.platform}</TableCell>
                  <TableCell>{account.account_name}</TableCell>
                  <TableCell>{account.account_id}</TableCell>
                  <TableCell>
                    {account.is_active ? 'Active' : 'Inactive'}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={account.token_assigned ? 'success.main' : 'error.main'}
                      fontWeight={500}
                    >
                      {account.token_assigned ? 'Token Assigned' : 'Token Unassigned'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {SHOW_INSTAGRAM_UI &&
                      String(account.platform || '').trim().toLowerCase() ===
                        'instagram' &&
                      account.is_active && (
                        <Tooltip title="Queue public Instagram scrape (Puppeteer)">
                          <span>
                            <IconButton
                              onClick={() => handleScrapePublicProfile(account)}
                              disabled={scrapingAccountId === account.id}
                              size="small"
                              color="primary"
                            >
                              {scrapingAccountId === account.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <CloudSync fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    <Tooltip title="Edit">
                      <span>
                        <IconButton onClick={() => handleOpenForm(account)}>
                          <Edit />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          onClick={() => handleDelete(account.id)}
                          disabled={deletingId === account.id}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Platform Account Dialog */}
      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Platform Account' : 'Add Platform Account'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              select
              label="Platform"
              name="platform"
              value={form.platform}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
              disabled={!!editingId}
              sx={{ minWidth: 180 }}
            >
              {platforms.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Account Name"
              name="account_name"
              value={form.account_name}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
            />
            <TextField
              label="Account ID / Handle"
              name="account_id"
              value={form.account_id}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Access Token"
              name="access_token"
              value={form.access_token || ''}
              onChange={handleChange}
              fullWidth
              margin="normal"
              type="password"
              placeholder={editingId ? 'Leave blank to keep current token' : 'Paste token from OAuth or use Facebook Page selector in Client Details'}
              helperText={editingId ? 'Token is not shown for security. Re-assign the page in Client Details (Info tab) to refresh the token.' : undefined}
            />
            <TextField
              label="Profile URL"
              name="profile_url"
              value={form.profile_url}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseForm}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingId ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete platform account"
        message="Are you sure you want to delete this platform account?"
        confirmLabel="Delete"
        loading={Boolean(deletingId)}
        onCancel={() => {
          if (!deletingId) setDeleteConfirm({ open: false, accountId: null });
        }}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
}
